import { z } from "zod";

/** The one public mutable pointer for the compiled Local Agent release train. */
export const SaleskoAgentDownloadsPrefix = "https://downloads.salesko.ai/salesko-agent/" as const;
export const SaleskoAgentManifestUrl = `${SaleskoAgentDownloadsPrefix}latest.json` as const;
export const SaleskoAgentBinaryName = "salesko-agent" as const;
/** Sole mutable operator symlink; never an attested executable or a service pin.
 * Install/activation may replace the link, never the selected release bytes.
 */
export const SaleskoAgentInstallPath = "/usr/local/bin/salesko-agent" as const;
/** Owner-approved system release root. Filesystem trust is checked by the Host. */
export const SaleskoAgentReleasesRoot = "/Library/Application Support/Salesko/releases" as const;
export const SaleskoAgentReleaseRecordName = "salesko-agent.installed.json" as const;
export const SaleskoAgentReleasePendingRecordName = ".installed-record.pending" as const;
export const SaleskoAgentPiEntrypointName = "salesko-agent-pi" as const;
export const SaleskoAgentSourceBundleName = "salesko-agent.mjs" as const;
export const SaleskoAgentInterpreterName = "bun" as const;
export const SaleskoAgentReleaseArchitectures = ["darwin-arm64", "darwin-x64"] as const;
/** The release-owned directory the attested runtime launches as `PI_PACKAGE_DIR`.
 * It is the only place the pi package identity, themes and export resources may
 * come from: a writable copy under a user home is never an execution identity.
 */
export const SaleskoAgentPiAssetsDirectoryName = "pi" as const;
/**
 * The sealed file set inside that directory, relative to it, in the strict
 * ascending order the SDK's asset list requires.
 *
 * Three kinds, all consumed by the official Pi 0.87.1 runtime when it runs as
 * a compiled binary: `package.json` is the identity the SDK's native provenance
 * verification re-reads; `theme/*.json` is what `getThemesDir()` loads for rpc
 * mode; `export-html/*` is the five-file set the native export layout requires.
 */
export const SaleskoAgentPiAssetPaths = Object.freeze([
  "export-html/template.css",
  "export-html/template.html",
  "export-html/template.js",
  "export-html/vendor/highlight.min.js",
  "export-html/vendor/marked.min.js",
  "package.json",
  "theme/dark.json",
  "theme/light.json"
] as const);

/** One sealed pi asset, declared by digest. `path` is relative to the pi assets directory. */
export const SaleskoAgentPiAssetSchema = z
  .object({
    path: z.string().min(1).max(1_024),
    sha256: z.string().regex(/^[0-9a-f]{64}$/u)
  })
  .strict();
export type SaleskoAgentPiAsset = z.infer<typeof SaleskoAgentPiAssetSchema>;

/**
 * The sealed pi asset list of one release: non-empty, strictly ascending by
 * `path`, and exactly the declared set. Unknown, missing, repeated or
 * out-of-order entries are not repaired into one.
 */
export function parseSaleskoAgentPiAssets(value: unknown): readonly SaleskoAgentPiAsset[] {
  const assets = z.array(SaleskoAgentPiAssetSchema).min(1).parse(value);
  const declared = new Set<string>(SaleskoAgentPiAssetPaths);
  let previous: string | undefined;
  for (const asset of assets) {
    if (!declared.has(asset.path)) {
      throw new Error(`installed release declares an unknown pi asset: ${asset.path}`);
    }
    if (previous !== undefined && asset.path <= previous) {
      throw new Error(`installed release pi assets are not strictly ascending at: ${asset.path}`);
    }
    previous = asset.path;
  }
  if (assets.length !== SaleskoAgentPiAssetPaths.length) {
    throw new Error("installed release does not declare the complete pi asset set");
  }
  return Object.freeze(assets.map(asset => Object.freeze({ ...asset })));
}

export const SaleskoAgentReleaseArchitectureSchema = z.enum(SaleskoAgentReleaseArchitectures);
export const SaleskoAgentReleaseVersionSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/u);
export const SaleskoAgentReleaseManifestSchema = z
  .object({
    version: SaleskoAgentReleaseVersionSchema,
    assets: z
      .object({
        "darwin-arm64": z.string().min(1).max(1_024),
        "darwin-x64": z.string().min(1).max(1_024)
      })
      .strict()
  })
  .strict();

export type SaleskoAgentReleaseArchitecture = z.infer<typeof SaleskoAgentReleaseArchitectureSchema>;
export type SaleskoAgentReleaseManifest = z.infer<typeof SaleskoAgentReleaseManifestSchema>;

export type SaleskoAgentReleaseAsset = {
  readonly architecture: SaleskoAgentReleaseArchitecture;
  readonly url: string;
  readonly checksumUrl: string;
};

export function saleskoAgentArchitectureForMachine(machine: string): SaleskoAgentReleaseArchitecture {
  switch (machine) {
    case "arm64":
      return "darwin-arm64";
    case "x86_64":
      return "darwin-x64";
    default:
      throw new Error(`no Salesko Local Agent build for this architecture (uname -m reported '${machine}')`);
  }
}

export function parseSaleskoAgentReleaseManifest(value: unknown): SaleskoAgentReleaseManifest {
  return SaleskoAgentReleaseManifestSchema.parse(value);
}

export function resolveSaleskoAgentReleaseAsset(
  manifestInput: unknown,
  architecture: SaleskoAgentReleaseArchitecture
): SaleskoAgentReleaseAsset {
  const manifest = parseSaleskoAgentReleaseManifest(manifestInput);
  const candidate = manifest.assets[architecture];
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    throw new Error(`release manifest has an invalid ${architecture} URL`);
  }

  const expectedPath = `/salesko-agent/v${manifest.version}/${SaleskoAgentBinaryName}-${architecture}`;
  if (
    url.protocol !== "https:" ||
    url.origin !== "https://downloads.salesko.ai" ||
    !url.href.startsWith(SaleskoAgentDownloadsPrefix) ||
    url.pathname !== expectedPath ||
    url.search ||
    url.hash
  ) {
    throw new Error(
      `release manifest does not name the immutable ${architecture} asset under ${SaleskoAgentDownloadsPrefix}`
    );
  }

  return { architecture, url: url.href, checksumUrl: `${url.href}.sha256` };
}

export function parseSaleskoAgentPublishedChecksum(value: string): string {
  const matched = /^([0-9a-f]{64})  salesko-agent\n?$/u.exec(value);
  if (!matched) {
    throw new Error("published Salesko Local Agent checksum is malformed");
  }
  return matched[1]!;
}

/** Pure relative layout only: no filesystem, completion or attestation claim.
 * Host joins these paths under the approved root (explicit isolated test roots
 * stay in fixture seams), then proves parent/leaf identity and record ownership.
 */
export function saleskoAgentReleaseRelativePaths(versionInput: string, checksumInput: string) {
  const version = SaleskoAgentReleaseVersionSchema.parse(versionInput);
  const checksum = parseSaleskoAgentPublishedChecksum(`${checksumInput}  ${SaleskoAgentBinaryName}\n`);
  const directory = `${version}-${checksum.slice(0, 12)}`;
  return Object.freeze({
    directory,
    artifact: `${directory}/${SaleskoAgentBinaryName}`,
    record: `${directory}/${SaleskoAgentReleaseRecordName}`,
    pendingRecord: `${directory}/${SaleskoAgentReleasePendingRecordName}`,
    piEntrypoint: `${directory}/${SaleskoAgentPiEntrypointName}`,
    piAssets: `${directory}/${SaleskoAgentPiAssetsDirectoryName}`,
    bundle: `${directory}/${SaleskoAgentSourceBundleName}`,
    interpreter: `${directory}/${SaleskoAgentInterpreterName}`
  });
}
